import Bugsnag, { OnErrorCallback } from '@bugsnag/js';

/**
 * Under `strict`, TypeScript types a `catch (e)` binding as `unknown`
 * (useUnknownInCatchVariables), but Bugsnag's NotifiableError is `Error`. This
 * module is the one place that bridges the two, so call sites don't each invent
 * their own `e as Error` cast -- three of them already had.
 *
 * It matters beyond typing: this codebase throws fetch `Response` objects as
 * well as Errors, and those used to reach Bugsnag as an unusable payload.
 */

/**
 * Duck-typed on purpose. Rejected requests can carry either the global Response
 * or portable-fetch's stand-in, and `instanceof` does not span both.
 */
export const isResponse = (e: unknown): e is Response =>
  typeof e === 'object' && e !== null && 'status' in e && 'json' in e;

const describe = (e: unknown): string => {
  if (isResponse(e)) {
    return `HTTP ${e.status}${e.statusText ? ` ${e.statusText}` : ''}`;
  }

  try {
    return JSON.stringify(e) ?? String(e);
  } catch {
    // Circular structures, getters that throw, etc.
    return String(e);
  }
};

/** Narrow an unknown thrown value to an Error, keeping what it said. */
export const toError = (e: unknown): Error =>
  e instanceof Error ? e : new Error(typeof e === 'string' ? e : describe(e));

/** The message to show a user for an unknown thrown value. */
export const getErrorMessage = (e: unknown): string => toError(e).message;

/**
 * Report a thrown value to Bugsnag. Use this instead of `Bugsnag.notify`, which
 * cannot accept an `unknown`.
 */
export const reportError = (e: unknown, onError?: OnErrorCallback): void =>
  Bugsnag.notify(toError(e), onError);
